import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** `2026-02-14` for a Date, without timezone drift (the API is naive-local). */
export function toISODate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** `2026-02-14T09:30:00` - what DRF expects for naive datetimes. */
export function toISODateTime(date: string, time: string): string {
  return `${date}T${time.length === 5 ? `${time}:00` : time}`
}

export function parseNaive(value: string | null): Date | null {
  if (!value) return null
  // Strip a trailing Z / offset - the backend stores local wall-clock time.
  const clean = value.replace(/(Z|[+-]\d{2}:?\d{2})$/, '')
  const d = new Date(clean)
  return Number.isNaN(d.getTime()) ? null : d
}

export function formatTime(value: string | null): string {
  const d = parseNaive(value)
  if (!d) return ''
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/**
 * Display locale for dates and numbers. Follows the language switcher, so a
 * French UI does not end up printing Slovak month names and decimal commas.
 * Held module-level for the same reason as `activeCurrency` below - the call
 * sites are plain `formatDate(x)` all over the app.
 */
const LOCALES: Record<string, string> = {
  sk: 'sk-SK',
  en: 'en-GB',
  de: 'de-DE',
  fr: 'fr-FR',
  it: 'it-IT',
  pl: 'pl-PL',
  cs: 'cs-CZ',
}
let activeLocale = LOCALES.sk

export function setActiveLocale(lang: string | null | undefined) {
  activeLocale = LOCALES[(lang ?? '').slice(0, 2)] ?? LOCALES.sk
}

export function getActiveLocale() {
  return activeLocale
}

export function formatDate(value: string | null, locale = activeLocale): string {
  const d = parseNaive(value)
  return d ? d.toLocaleDateString(locale) : ''
}

export function formatDateTime(value: string | null, locale = activeLocale): string {
  const d = parseNaive(value)
  return d ? `${d.toLocaleDateString(locale)} ${formatTime(value)}` : ''
}

/**
 * Billing currency, from settings.currency_key. Held module-level so the many
 * plain `formatMoney(x)` call sites keep working; AppLayout sets it once the
 * bootstrap query resolves.
 */
let activeCurrency = 'EUR'

export function setActiveCurrency(code: string | null | undefined) {
  if (code) activeCurrency = code.toUpperCase()
}

export function getActiveCurrency() {
  return activeCurrency
}

/** Just the symbol, for compact table cells: "€", "Kč", "zł". */
export function currencySymbol(code = activeCurrency): string {
  const parts = new Intl.NumberFormat(activeLocale, {
    style: 'currency',
    currency: code,
  }).formatToParts(0)
  return parts.find((p) => p.type === 'currency')?.value ?? code
}

export function formatMoney(
  value: string | number | null | undefined,
  currency = activeCurrency,
): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'string' ? Number.parseFloat(value) : value
  if (Number.isNaN(n)) return '—'
  return new Intl.NumberFormat(activeLocale, { style: 'currency', currency }).format(n)
}

/** Číslo vo formáte jazyka aplikácie: „1,5“ / „1.5“ / „1 234,5“. */
export function formatNumber(
  value: string | number | null | undefined,
  { min = 0, max = 2 }: { min?: number; max?: number } = {},
): string {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'string' ? Number.parseFloat(value) : value
  if (Number.isNaN(n)) return ''
  return new Intl.NumberFormat(activeLocale, {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  }).format(n)
}

/** Suma bez meny, vždy na dve desatinné miesta: „25,50“ / „25.50“. */
export function formatAmount(value: string | number | null | undefined): string {
  return formatNumber(value, { min: 2, max: 2 })
}

export function minutesToHours(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m ? `${h}:${String(m).padStart(2, '0')}` : `${h}`
}

export function addDays(date: Date, days: number): Date {
  const copy = new Date(date)
  copy.setDate(copy.getDate() + days)
  return copy
}

export function startOfWeek(date: Date): Date {
  const copy = new Date(date)
  const day = (copy.getDay() + 6) % 7 // Monday = 0
  copy.setDate(copy.getDate() - day)
  copy.setHours(0, 0, 0, 0)
  return copy
}

/**
 * Which 1-based calendar slot a datetime falls into.
 * Inverse of `reservations.services.slot_to_datetime`.
 */
export function timeToSlot(value: string | null, startHour: number, interval: number): number | null {
  const d = parseNaive(value)
  if (!d) return null
  const hours = d.getHours() + d.getMinutes() / 60
  return Math.round((hours - startHour) / interval) + 1
}

/** How many slots a reservation spans (minimum 1). */
export function slotSpan(start: string | null, end: string | null, interval: number): number {
  const s = parseNaive(start)
  const e = parseNaive(end)
  if (!s || !e) return 1
  return Math.max(1, Math.round((e.getTime() - s.getTime()) / 3600000 / interval))
}

export function parseIds(value: string | null | undefined): number[] {
  if (!value) return []
  return value
    .split(',')
    .map((x) => Number.parseInt(x.trim(), 10))
    .filter((n) => !Number.isNaN(n))
}

export type DateRange = { from: string; to: string }

/**
 * A season runs from 31 July to 31 July - the rule the backend uses in
 * `apps.core.services.season_bounds`. Kept in sync deliberately: the quick
 * range buttons must select exactly what the server would.
 */
export function seasonRange(offset = 0, today = new Date()): DateRange {
  const startYear = (today.getMonth() >= 6 ? today.getFullYear() : today.getFullYear() - 1) + offset
  return {
    from: toISODate(new Date(startYear, 6, 31)),
    to: toISODate(new Date(startYear + 1, 6, 31)),
  }
}

/** Named ranges for the "dnes / týždeň / mesiac / sezóna" shortcuts. */
export function quickRange(
  key: 'today' | 'week' | 'month' | 'season',
  today = new Date(),
): DateRange {
  switch (key) {
    case 'today': {
      const day = toISODate(today)
      return { from: day, to: day }
    }
    case 'week': {
      const start = startOfWeek(today)
      return { from: toISODate(start), to: toISODate(addDays(start, 6)) }
    }
    case 'month':
      return {
        from: toISODate(new Date(today.getFullYear(), today.getMonth(), 1)),
        // Day 0 of the next month is the last day of this one.
        to: toISODate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      }
    case 'season':
      return seasonRange(0, today)
  }
}

/**
 * Step a range forwards or backwards by its own length.
 *
 * The shape is preserved rather than blindly adding days: a calendar month
 * steps to the next calendar month (so 1.-31. August becomes 1.-30. September,
 * not 1.-31. September), and a season steps by a whole year. Everything else
 * slides by however many days it spans, which makes a week behave like a week.
 */
export function shiftRange(range: DateRange, direction: 1 | -1): DateRange {
  const from = new Date(`${range.from}T00:00:00`)
  const to = new Date(`${range.to}T00:00:00`)
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return range

  const isFirstOfMonth = from.getDate() === 1
  const isLastOfMonth =
    to.getDate() === new Date(to.getFullYear(), to.getMonth() + 1, 0).getDate()

  if (isFirstOfMonth && isLastOfMonth) {
    const months =
      (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth()) + 1
    const nextFrom = new Date(from.getFullYear(), from.getMonth() + direction * months, 1)
    const nextTo = new Date(nextFrom.getFullYear(), nextFrom.getMonth() + months, 0)
    return { from: toISODate(nextFrom), to: toISODate(nextTo) }
  }

  // 31 July -> 31 July is the season boundary (see seasonRange).
  const isSeason =
    from.getMonth() === 6 && from.getDate() === 31 && to.getMonth() === 6 && to.getDate() === 31
  if (isSeason) {
    return {
      from: toISODate(new Date(from.getFullYear() + direction, 6, 31)),
      to: toISODate(new Date(to.getFullYear() + direction, 6, 31)),
    }
  }

  const days = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1
  return {
    from: toISODate(addDays(from, direction * days)),
    to: toISODate(addDays(to, direction * days)),
  }
}

/** Readable text colour for an arbitrary background. */
export function contrastText(hex?: string | null): string {
  if (!hex || !hex.startsWith('#')) return '#111827'
  const v = hex.slice(1)
  const full = v.length === 3 ? v.split('').map((c) => c + c).join('') : v
  const r = Number.parseInt(full.slice(0, 2), 16)
  const g = Number.parseInt(full.slice(2, 4), 16)
  const b = Number.parseInt(full.slice(4, 6), 16)
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#111827' : '#ffffff'
}

/**
 * Preložený názov jazyka podľa jeho kódu (`languageNames.SK` a pod.).
 *
 * `languages.name` v databáze je vždy po slovensky - je to hodnota
 * zapísaná pri zakladaní školy, nie preklad podľa UI jazyka. Prepínač
 * jazyka v pravom hornom rohu mení jazyk rozhrania, ale bez tohto by
 * zoznam jazykov výučby zostal po slovensky aj v anglickom UI. Kód, ktorý
 * v `languageNames` chýba, sa zobrazí pod pôvodným (slovenským) názvom.
 */
export function languageName(
  t: (key: string, options?: { defaultValue?: string }) => string,
  key: string | null | undefined,
  fallback: string | null | undefined,
): string {
  if (!key) return fallback ?? ''
  return t(`languageNames.${key}`, { defaultValue: fallback ?? key })
}

/** Koľko znakov poznámky sa vypisuje, kým sa zvyšok schová. */
export const NOTE_LIMIT = 300

/**
 * Skrátiť dlhý text pre výpis.
 *
 * Poznámka pri lekcii je od `sql/013` `TEXT`, takže môže mať aj dva odstavce.
 * V detaile je to v poriadku, v riadku kalendára alebo v bunke tabuľky nie —
 * rozhodí layout a prekryje to, čo je vedľa.
 *
 * Reže sa **na hranici slova**: useknutá polovica slova vyzerá ako chyba
 * aplikácie, nie ako skrátený text. Keď v poslednej štvrtine limitu žiadna
 * medzera nie je (dlhá adresa, súvislý reťazec), reže sa natvrdo — inak by sa
 * limit dal obísť textom bez medzier.
 */
export function truncate(value: string | null | undefined, limit = NOTE_LIMIT): string {
  const text = (value ?? '').trim()
  if (text.length <= limit) return text

  const cut = text.slice(0, limit)
  const space = cut.lastIndexOf(' ')
  return `${space > limit * 0.75 ? cut.slice(0, space) : cut}…`
}
